/**
 * rendererRegistry — Maps GamificationType to lazy-loaded renderer components
 *
 * Adding a new gamification type requires only:
 * 1. Create the renderer component in ./types/
 * 2. Add the lazy import + registration here
 *
 * All renderers receive GamificationRendererProps (model data + display mode).
 */
import {lazy, ComponentType} from "react";
import {GamificationModelResponse} from "@/hooks/gamification/useGetGamificationModel";

/**
 * Display mode for a gamification renderer:
 * - "full"    — complete interactive experience (full card on hub page)
 * - "compact" — summary view (embedded in a feed or sidebar widget)
 * - "micro"   — minimal inline indicator (a badge, count, or icon)
 * - "overlay" — reserved for Overwolf in-game overlay; renderers that do not
 *               implement it should fall back to "compact"
 */
export type GamificationDisplayMode = "full" | "compact" | "micro" | "overlay";

export interface GamificationRendererProps {
    model: GamificationModelResponse;
    displayMode: GamificationDisplayMode;
}

type LazyRenderer = React.LazyExoticComponent<ComponentType<GamificationRendererProps>>;

const registry = new Map<string, LazyRenderer>();

/**
 * Register a lazy-loaded renderer for a gamification type.
 * Called at module scope — not inside components.
 */
export function registerRenderer(type: string, loader: () => Promise<{ default: ComponentType<GamificationRendererProps> }>) {
    registry.set(type, lazy(loader));
}

/**
 * Get the renderer component for a given type.
 * Returns undefined if no renderer is registered (type not yet implemented).
 */
export function getRenderer(type: string): LazyRenderer | undefined {
    return registry.get(type);
}

/**
 * Check if a renderer exists for the given type.
 */
export function hasRenderer(type: string): boolean {
    return registry.has(type);
}

/*
 * ============================================
 * Type Registrations
 * ============================================
 *
 * Register renderers here as types are implemented.
 * Each type's renderer lives in ./types/<TypeName>Renderer.tsx
 *
 * Phase 3: WHEEL_SPIN
 * Phase 4: LOCK_IN, BINGO
 */
registerRenderer("WHEEL_SPIN", () => import("./types/WheelSpinRenderer"));
registerRenderer("LOCK_IN", () => import("./types/LockInRenderer"));
registerRenderer("BINGO", () => import("./types/BingoRenderer"));
registerRenderer("SCAVENGER_HUNT", () => import("./types/ScavengerHuntRenderer"));
registerRenderer("TRIVIA", () => import("./types/TriviaRenderer"));
registerRenderer("PREDICTIONS", () => import("./types/PredictionsRenderer"));
registerRenderer("DAILY_DARE", () => import("./types/DailyDareRenderer"));
registerRenderer("STREAK_BOARD", () => import("./types/StreakBoardRenderer"));
registerRenderer("RIVALRY", () => import("./types/RivalryRenderer"));
registerRenderer("BOUNTY", () => import("./types/BountyRenderer"));
registerRenderer("COLLECTION", () => import("./types/CollectionRenderer"));
registerRenderer("SURVIVAL", () => import("./types/SurvivalRenderer"));
registerRenderer("SHOWCASE", () => import("./types/ShowcaseRenderer"));
registerRenderer("BRACKET_VOTE", () => import("./types/BracketVoteRenderer"));
registerRenderer("HOT_SEAT", () => import("./types/HotSeatRenderer"));
registerRenderer("PIG_HORSE", () => import("./types/PigHorseRenderer"));
registerRenderer("BOSS_BATTLE", () => import("./types/BossBattleRenderer"));
registerRenderer("FLASH_EVENT", () => import("./types/FlashEventRenderer"));
registerRenderer("MYSTERY_BOX", () => import("./types/MysteryBoxRenderer"));
registerRenderer("COUNTDOWN", () => import("./types/CountdownRenderer"));
registerRenderer("RELAY", () => import("./types/RelayRenderer"));
registerRenderer("TURF_WAR", () => import("./types/TurfWarRenderer"));
registerRenderer("QUEST_ROULETTE", () => import("./types/QuestRouletteRenderer"));
registerRenderer("SABOTEUR", () => import("./types/SaboteurRenderer"));
registerRenderer("CIPHER", () => import("./types/CipherRenderer"));
registerRenderer("MARKET", () => import("./types/MarketRenderer"));
